"use client"

import { Images } from "@/constants/images"
import { t } from "@/languages/i18n"
import { getSettings } from "@/lib/navigation-services"
import { ComponentConfig } from "@measured/puck"
import { DatePicker, Form, Input, Select, Spin } from "antd"
import TextArea from "antd/es/input/TextArea"
import Image from "next/image"
import { useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"
import { DataSettingItem } from "../../../types"
import "./index.css"

export interface FormCustomizeTour extends CommonStylesProps {
  messageSuccess?: string
  messageFail?: string
  title: string
  description: string
  isShowTitle: boolean
  isShowDescription: boolean
}

export const RenderConfig: ComponentConfig<FormCustomizeTour> = {
  render: ({
    styles,
    responsiveType,
    className,
    messageFail,
    messageSuccess,
    title,
    description,
    isShowTitle,
    isShowDescription,
  }) => {
    const id = `form-register-${Math.random().toString(36).substr(2, 9)}`
    const [form] = Form.useForm()
    const [isPopupVisible, setIsPopupVisible] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [message, setMessage] = useState("")

    const [formData, setFormData] = useState({
      emailTo: "",
      name: "",
      email: "",
      phone: "",
      riders: "",
      start: null,
      end: null,
      location: "",
      adventureType: "",
      accommodationType: "",
      accommodation: "",
      distance: "",
      difficulty: "",
      difficultyelevation: "",
      additionalInfo: "",
    })

    const [dynamicTitle, setDynamicTitle] = useState(title)

    // Update title based on hash fragment
    useEffect(() => {
      if (typeof window !== "undefined") {
        const hash = window.location.hash.substring(1)
        const currentLocale = document.documentElement.lang || "en"
        const isVI = currentLocale?.toLowerCase().startsWith("vi")
        if (hash) {
          try {
            const decodedHash = decodeURIComponent(hash)
            if (isVI) {
              setDynamicTitle(t(`Thiết kế tour ${decodedHash.charAt(0).toUpperCase() + decodedHash.slice(1)} của bạn`))
            } else {
              setDynamicTitle(t(`Design Your ${decodedHash.charAt(0).toUpperCase() + decodedHash.slice(1)} Tour`))
            }
          } catch (e) {
            // If decoding fails, use original hash
            if (isVI) {
              setDynamicTitle(t(`Thiết kế tour ${hash.charAt(0).toUpperCase() + hash.slice(1)} của bạn`))
            } else {
              setDynamicTitle(t(`Design Your ${hash.charAt(0).toUpperCase() + hash.slice(1)} Tour`))
            }
          }
        }
      } else {
        setDynamicTitle(title)
      }
    }, [title])

    const fetchSetting = async () => {
      try {
        const response = await getSettings()
        const emailTo = response.find((item: DataSettingItem) => item.attributes.key === "emailFormItinerary")
          ?.attributes.value
        setFormData(prev => ({ ...prev, emailTo: emailTo }))
      } catch (error) {
        console.error("Error fetching setting data:", error)
      }
    }

    useEffect(() => {
      fetchSetting()
    }, [])

    const closePopup = () => {
      setIsPopupVisible(false)
      form.resetFields()
    }

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()

      try {
        setIsLoading(true)
        await form.validateFields()
        const emailData = {
          subject: "[Thông báo] Đăng ký tour",
          emailTo: formData.emailTo,
          content: `
            Họ và tên: ${form.getFieldValue("name") || "Không xác định"}
            Email: ${form.getFieldValue("email") || "Không xác định"}
            Số điện thoại: ${form.getFieldValue("phone") || "Không xác định"}
            Số lượng người: ${form.getFieldValue("riders") || "Không xác định"}
            Ngày bắt đầu: ${form.getFieldValue("start")?.format("DD/MM/YYYY") || "Không xác định"}
            Ngày kết thúc: ${form.getFieldValue("end")?.format("DD/MM/YYYY") || "Không xác định"}
            Nơi bạn muốn tham quan: ${form.getFieldValue("location") || "Không xác định"}
            Phong cách lưu trú phù hợp: ${form.getFieldValue("accommodationType") || "Không xác định"}
            Phong cách lưu trú phù hợp: ${form.getFieldValue("accommodation") || "Không xác định"}
            Mức độ thử thách mong muốn: ${form.getFieldValue("difficulty") || "Không xác định"}
            Khoảng cách muốn đi hàng ngày: ${form.getFieldValue("difficultyelevation") || "Không xác định"}
            Yêu cầu bổ sung: ${form.getFieldValue("additionalInfo") || "Không có"}
          `,
        }

        const response = await fetch("/api/email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(emailData),
        })
        if (!response.ok) {
          throw new Error("Failed to submit data")
        }
        setMessage(messageSuccess || t("Đăng ký thành công!"))
        setIsPopupVisible(true)
        setFormData({
          emailTo: "",
          name: "",
          email: "",
          phone: "",
          riders: "",
          start: null,
          accommodation: "",
          distance: "",
          end: null,
          location: "",
          adventureType: "",
          accommodationType: "",
          difficulty: "",
          difficultyelevation: "",
          additionalInfo: "",
        })
        form.resetFields()
      } catch (error) {
        console.error("Error:", error)
        setMessage(messageFail || t("Đã xảy ra lỗi khi đăng ký!"))
      } finally {
        setIsLoading(false)
      }
    }

    const mergedStyles = {
      mobile: { ...styles?.mobile },
      tablet: { ...styles?.tablet },
      desktop: { ...styles?.desktop },
    }

    const responsiveCSS = generateResponsiveCSS(id, mergedStyles || {}, responsiveType)

    return (
      <>
        {responsiveCSS}
        <div className={`${id} ${twMerge(className)} w-full text-center form-cutomize-tour`}>
          {/* {isShowTitle || */}
          <div className="flex flex-col gap-2 sm:gap-3 mb-10 sm:mb-16">
            {isShowTitle && (
              <h1 className="sm:text-left text-center sm:text-[54px] sm:leading-[72px] text-[40px] leading-[54px] text-[#242F3E] font-bold">
                {dynamicTitle}
              </h1>
            )}
            {isShowDescription && (
              <p className="text-base leading-[27px] text-center sm:text-left text-[#4F4F4F] font-normal">
                {description}
              </p>
            )}
          </div>
          {/* ))} */}
          <div className="w-full text-center">
            <Form
              form={form}
              layout="vertical"
              labelAlign="right"
              onFinish={handleSubmit}
              className="mx-auto flex sm:gap-16 flex-col sm:flex-row gap-4"
            >
              <div className="w-full sm:w-1/2 flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <Form.Item
                    name="name"
                    label={t("Your name")}
                    className="text-sm leading-[22px] font-medium text-[#242F3E]"
                    rules={[{ required: true, message: t("Please enter your name") }]}
                  >
                    <Input
                      placeholder={t("Your name")}
                      className="h-10 !rounded-lg placeholder:text-base placeholder:leading-[22px] placeholder:font-medium"
                    />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label={t("Email")}
                    className="email-field text-sm leading-[22px] font-medium text-[#242F3E]"
                  >
                    <Input placeholder={t("Email")} className="h-10 !rounded-lg" />
                  </Form.Item>
                  <Form.Item
                    name="phone"
                    label={t("Phone")}
                    className="text-sm leading-[22px] font-medium text-[#242F3E]"
                    rules={[{ required: true, message: t("Please enter your phone number") }]}
                  >
                    <Input
                      placeholder={t("Phone")}
                      className="h-10 !rounded-lg placeholder:text-base
                    placeholder:leading-[22px]
                    placeholder:font-medium"
                    />
                  </Form.Item>

                  <Form.Item
                    name="start"
                    label={t("Start")}
                    className="text-sm leading-[22px] font-medium text-[#242F3E]"
                    rules={[{ required: true, message: t("Please select a start date") }]}
                  >
                    <DatePicker placeholder="mm/dd/yy" className="w-full h-10 !rounded-lg" format="MM/DD/YYYY" />
                  </Form.Item>
                  <Form.Item
                    name="end"
                    label={t("End")}
                    rules={[{ required: true, message: t("Please select an end date") }]}
                  >
                    <DatePicker
                      className="w-full h-10 !rounded-lg placeholder:text-base placeholder:leading-[22px] placeholder:font-medium"
                      format="DD/MM/YYYY"
                      placeholder="mm/dd/yy"
                    />
                  </Form.Item>
                  <Form.Item
                    name="riders"
                    label={t("How many riders")}
                    className="text-sm leading-[22px] font-medium text-[#242F3E]"
                    rules={[{ required: true, message: t("Please enter riders") }]}
                  >
                    <Input
                      placeholder={t("How many riders")}
                      className="h-10 !rounded-lg placeholder:text-base placeholder:leading-[22px] !placeholder:font-montserrat !placeholder:font-medium"
                    />
                  </Form.Item>
                </div>
                <div className="flex flex-col gap-3">
                  <Form.Item
                    name="location"
                    label={t("Where would you like to visit?")}
                    className="text-sm leading-[22px] font-medium text-[#242F3E]"
                  >
                    <TextArea
                      placeholder={t("Message")}
                      className="!rounded-lg !h-[92px] placeholder:text-base placeholder:leading-[22px] placeholder:font-medium font-Josefin_Sans"
                    />
                  </Form.Item>
                  {/* <Form.Item
                    name="adventureType"
                    label={t("What sort of adventure would suit you?")}
                    className="text-sm leading-[22px] font-medium text-[#242F3E]"
                    rules={[{ required: true, message: t("Please select an adventure type") }]}
                  >
                    <Select
                      placeholder={t("Excursion")}
                      className="h-10 !rounded-lg placeholder:text-base placeholder:leading-[22px] placeholder:font-medium"
                    >
                      <Select.Option value="excursion">{t("Excursion")}</Select.Option>
                      <Select.Option value="trekking">{t("Trekking")}</Select.Option>
                      <Select.Option value="cycling">{t("Cycling")}</Select.Option>
                    </Select>
                  </Form.Item> */}
                  <Form.Item
                    name="accommodationType"
                    label={t("What style of accommodation would suit you?")}
                    className="text-sm leading-[22px] font-medium text-[#242F3E]"
                    rules={[{ required: true, message: t("Please select accommodation type") }]}
                  >
                    <Select
                      placeholder={t("5-star")}
                      className="h-10 !rounded-lg placeholder:text-base placeholder:leading-[22px] placeholder:font-medium"
                    >
                      <Select.Option value="5-star">{t("5-star")}</Select.Option>
                      <Select.Option value="4-star">{t("4-star")}</Select.Option>
                      <Select.Option value="3-star">{t("3-star")}</Select.Option>
                      <Select.Option value="2-star">{t("2-star")}</Select.Option>
                      <Select.Option value="1-star">{t("1-star")}</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
              </div>
              <div className="w-full sm:w-1/2 flex flex-col gap-3 ">
                <Form.Item
                  name="difficulty"
                  label={t("How challenging would you like the tour?")}
                  className="text-sm leading-[22px] font-medium text-[#242F3E]"
                  rules={[{ required: true, message: t("Please select difficulty level") }]}
                >
                  <Select
                    placeholder={t("Easy")}
                    className="h-10 !rounded-lg placeholder:text-base placeholder:leading-[22px] placeholder:font-medium"
                  >
                    <Select.Option value="easy">{t("Easy")}</Select.Option>
                    <Select.Option value="moderate">{t("Moderate")}</Select.Option>
                    <Select.Option value="hard">{t("Hard")}</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="accommodation"
                  label={t("What style of accommodation would suit you?")}
                  className="text-sm leading-[22px] font-medium text-[#242F3E]"
                  rules={[{ required: true, message: t("Please select accommodation type") }]}
                >
                  <Select
                    placeholder={t("5-star")}
                    className="h-10 !rounded-lg placeholder:text-base placeholder:leading-[22px] placeholder:font-medium"
                  >
                    <Select.Option value="5-star">{t("5-star")}</Select.Option>
                    <Select.Option value="4-star">{t("4-star")}</Select.Option>
                    <Select.Option value="3-star">{t("3-star")}</Select.Option>
                  </Select>
                </Form.Item>

                {/* <Form.Item
                  name="distance"
                  label={t("What sort of adventure would suit you?")}
                  className="text-sm leading-[22px] font-medium text-[#242F3E]"
                  rules={[{ required: true, message: t("Please select an adventure type") }]}
                >
                  <Select
                    placeholder={t("Excursion")}
                    className="h-10 !rounded-lg placeholder:text-base placeholder:leading-[22px] placeholder:font-medium"
                  >
                    <Select.Option value="excursion">{t("Excursion")}</Select.Option>
                    <Select.Option value="trekking">{t("Trekking")}</Select.Option>
                    <Select.Option value="cycling">{t("Cycling")}</Select.Option>
                  </Select>
                </Form.Item> */}

                <Form.Item
                  name="difficultyelevation"
                  label={t("What sort of distances would you like to ride daily?")}
                  className="text-sm leading-[22px] font-medium text-[#242F3E]"
                  rules={[{ required: true, message: t("Please select a distance range") }]}
                >
                  <Select
                    placeholder="<10 km"
                    className="h-10 !rounded-lg placeholder:text-base placeholder:leading-[22px] placeholder:font-medium"
                  >
                    <Select.Option value="<10">&lt;10 km</Select.Option>
                    <Select.Option value="10-20">10-20 km</Select.Option>
                    <Select.Option value="20-50">20-50 km</Select.Option>
                    <Select.Option value=">50">&gt;50 km</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="additionalInfo"
                  label={t("Let us know your requirements.")}
                  className="text-sm leading-[22px] font-medium text-[#242F3E]"
                >
                  <TextArea
                    placeholder={t("Message")}
                    className="!rounded-lg !h-[92px] placeholder:text-base placeholder:leading-[22px] placeholder:font-medium font-Josefin_Sans"
                  />
                </Form.Item>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="flex justify-center items-center gap-2 w-full mt-5 bg-[#0B9444] font-semibold text-base leading-[22px] text-white py-[13px] rounded-xl hover:bg-[#007B73]"
                >
                  <Image src={Images.iconSubmit} className="h-5 w-5" alt="icon submit" />
                  {t("Submit")}
                </button>
              </div>
            </Form>
          </div>

          {isLoading && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <Spin />
            </div>
          )}

          {isPopupVisible && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="bg-white p-8 rounded shadow-md relative">
                <button
                  onClick={closePopup}
                  className="absolute top-0 text-[30px] right-2 text-gray-600 hover:text-gray-800"
                  aria-label="Close"
                >
                  &times;
                </button>
                <p className="text-lg font-semibold">{message}</p>
              </div>
            </div>
          )}
        </div>
      </>
    )
  },
}
